<template>
  <v-container>
    <v-card class="mx-auto mt-5" max-width="800">
      <v-card-title class="headline d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-briefcase-search</v-icon>
        {{ $t('Kofferauswahl') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('Bitte wählen Sie einen Koffer aus, um zu den Prüfstellen zu gelangen.') }}
      </v-card-subtitle>
      <v-divider></v-divider>
      
      <v-list v-if="cases && cases.length > 0" class="py-0">
        <v-list-item
          v-for="caseItem in cases"
          :key="caseItem._id"
          @click="selectCase(caseItem)"
          link
          class="border-bottom"
        >
          <template v-slot:prepend>
            <v-icon color="secondary">mdi-briefcase-variant</v-icon>
          </template>
          <v-list-item-title class="font-weight-bold">{{ caseItem.title }}</v-list-item-title>
          <v-list-item-subtitle v-if="caseItem._id" class="text-caption">ID: {{ caseItem._id }}</v-list-item-subtitle>
          <template v-slot:append>
            <v-icon color="grey-lighten-1">mdi-chevron-right</v-icon>
          </template>
        </v-list-item>
      </v-list>
      
      <v-card-text v-else-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4">{{ $t('Suche Koffer...') }}</div>
      </v-card-text>
      
      <v-card-text v-else class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1">mdi-briefcase-remove</v-icon>
        <div class="mt-4 text-h6 text-grey">{{ $t('Keine Koffer für diesen Benutzer gefunden.') }}</div>
        <v-btn color="primary" class="mt-4" variant="outlined" @click="fetchCases">{{ $t('Aktualisieren') }}</v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import APIService from '@/services/APIService';
import APIServiceRestDB from '@/services/MesskursBackendService';
import { getLicenseIdsFromUser, getManageableLicenseIds, normalizeLicenseContacts, resolveId } from '@/services/messkursAuth';

export default {
  name: 'OwnerCaseSelectionView',
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const cases = ref([]);
    const loading = ref(false);

    // Ziel-Modus aus Query-Parameter oder Standard "Lernen"
    const targetMode = computed(() => {
      return route.query.mode || 'Lernen';
    });

    const fetchCases = async () => {
      loading.value = true;
      try {
        const user = store.state.user;
        const userId = resolveId(user);
        if (!userId) {
          console.error("No user ID found for fetching cases. User object:", store.state.user);
          loading.value = false;
          return;
        }

        const contactsResponse = await APIService.getUserLicenseContacts().catch(() => []);
        const licenseContacts = normalizeLicenseContacts(contactsResponse);
        const manageableLicenseIds = getManageableLicenseIds(user, licenseContacts);
        const userLicenseIds = getLicenseIdsFromUser(user);
        const scopedOwnerIds = [...new Set([...manageableLicenseIds, ...userLicenseIds])];

        console.log("Fetching cases for Messkurs scope:", { userId, scopedOwnerIds });
        const userCases = await APIServiceRestDB.getObjectsForMesskursScope(
          scopedOwnerIds,
          userId,
          userId
        );
        console.log("Found cases:", userCases);
        cases.value = userCases || [];
      } catch (error) {
        console.error("Error fetching owner cases:", error);
      } finally {
        loading.value = false;
      }
    };

    const selectCase = async (caseItem) => {
      const caseId = caseItem._id;
      // Set in store to make it persistent
      await store.dispatch('updateCaseId', caseId);
      await store.dispatch('updateCase', caseItem);
      
      // Clear session when a new case is selected via this view
      await store.dispatch('updateSession', null);
      await store.dispatch('updateSessionId', null);

      // Navigiere zum gewählten Modus mit der Case ID
      router.push({ name: targetMode.value, query: { case: caseId } });
    };

    onMounted(() => {
      fetchCases();
    });

    return {
      cases,
      loading,
      selectCase,
      fetchCases
    };
  }
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.border-bottom:last-child {
  border-bottom: none;
}
</style>
